import React, { useState, useEffect, useMemo } from 'react';
import { Search, RefreshCw, Calendar, AlertTriangle, Check, X, Loader2 } from 'lucide-react';
import DatePicker, { registerLocale } from 'react-datepicker';
import el from 'date-fns/locale/el';
import 'react-datepicker/dist/react-datepicker.css';
import { calculateDaysBetween } from '../utils/vacationUtils';

registerLocale('el', el);

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

const authFetch = (url, options = {}) => {
  const token = localStorage.getItem('auth_token');
  return fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(options.headers || {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
};

const CORRECTABLE_TYPES = ['vacation', 'unpaid'];

const TYPE_LABELS = {
  vacation: { label: 'Κανονική Άδεια', icon: '🏖️', color: '#3B82F6' },
  unpaid: { label: 'Άνευ Αποδοχών', icon: '💼', color: '#EA580C' },
};

const STATUS_LABELS = {
  pending: 'Εκκρεμεί',
  manager1_approved: 'Μερική Έγκριση (Δ1)',
  approved: 'Εγκρίθηκε',
  rejected: 'Απορρίφθηκε',
  manager1_rejected: 'Απορρίφθηκε (Δ1)',
};

const LeaveTypeCorrectionView = ({ holidays = [] }) => {
  const [allRequests, setAllRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  const [searchTerm, setSearchTerm] = useState('');
  const [startDateFilter, setStartDateFilter] = useState(null);
  const [endDateFilter, setEndDateFilter] = useState(null);

  const [confirmTarget, setConfirmTarget] = useState(null); // { request, newType }
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState('');

  const loadData = async () => {
    setLoading(true);
    setLoadError('');
    try {
      const res = await authFetch(`${API_BASE_URL}/vacation-requests`);
      const data = await res.json();
      if (!res.ok) {
        setLoadError(data.error || 'Αποτυχία φόρτωσης αιτήσεων');
        setAllRequests([]);
      } else {
        setAllRequests(Array.isArray(data) ? data : []);
      }
    } catch (err) {
      setLoadError('Σφάλμα σύνδεσης. Παρακαλώ δοκιμάστε ξανά.');
      setAllRequests([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(''), 3000);
  };

  const correctableRequests = useMemo(() => {
    return allRequests.filter(req => CORRECTABLE_TYPES.includes((req.leave_type || req.leaveType || '').toLowerCase()));
  }, [allRequests]);

  const filteredRequests = useMemo(() => {
    return correctableRequests
      .filter(req => {
        if (searchTerm) {
          const name = (req.user_name || '').toLowerCase();
          if (!name.includes(searchTerm.toLowerCase())) return false;
        }
        const start = new Date(req.start_date || req.startDate);
        const end = new Date(req.end_date || req.endDate);
        if (startDateFilter && end < startDateFilter) return false;
        if (endDateFilter && start > endDateFilter) return false;
        return true;
      })
      .sort((a, b) => new Date(b.start_date || b.startDate) - new Date(a.start_date || a.startDate));
  }, [correctableRequests, searchTerm, startDateFilter, endDateFilter]);

  const requestConversion = (request) => {
    const currentType = (request.leave_type || request.leaveType || '').toLowerCase();
    const newType = currentType === 'vacation' ? 'unpaid' : 'vacation';
    setConfirmTarget({ request, newType });
  };

  const confirmConversion = async () => {
    if (!confirmTarget) return;
    setSubmitting(true);
    try {
      const res = await authFetch(`${API_BASE_URL}/vacation-requests/${confirmTarget.request.id}/leave-type`, {
        method: 'PUT',
        body: JSON.stringify({ leaveType: confirmTarget.newType }),
      });
      const data = await res.json();
      if (data.success) {
        showToast('Ο τύπος άδειας ενημερώθηκε');
        setConfirmTarget(null);
        await loadData();
      } else {
        showToast(data.error || 'Αποτυχία ενημέρωσης');
        setConfirmTarget(null);
      }
    } catch (err) {
      showToast('Σφάλμα σύνδεσης. Παρακαλώ δοκιμάστε ξανά.');
      setConfirmTarget(null);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-gray-800 dark:text-jewel-text">Διόρθωση Τύπου Άδειας</h2>
        <p className="text-xs text-gray-500 dark:text-jewel-text-muted mt-1">
          Αναζητήστε μια αίτηση και διορθώστε τον τύπο της μεταξύ Κανονικής Άδειας και Άνευ Αποδοχών.
        </p>
      </div>

      {toast && (
        <div className="fixed top-4 right-4 z-50 bg-green-600 text-white text-sm font-medium px-4 py-2.5 rounded-lg shadow-lg flex items-center gap-2 animate-in fade-in slide-in-from-top-2">
          <Check className="h-4 w-4" />
          {toast}
        </div>
      )}

      <div className="bg-white dark:bg-jewel-bg p-4 rounded-xl border border-gray-200 dark:border-jewel-border flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 dark:text-jewel-text-muted" />
          <input
            type="text"
            placeholder="Αναζήτηση με όνομα υπαλλήλου..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 text-sm border border-gray-300 dark:border-jewel-border bg-white dark:bg-jewel-surface text-gray-900 dark:text-jewel-text rounded-lg outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div className="flex items-center gap-2">
          <Calendar className="h-4 w-4 text-gray-400 dark:text-jewel-text-muted flex-shrink-0" />
          <DatePicker
            selected={startDateFilter}
            onChange={setStartDateFilter}
            selectsStart
            startDate={startDateFilter}
            endDate={endDateFilter}
            locale="el"
            dateFormat="dd/MM/yyyy"
            placeholderText="Από"
            className="px-3 py-2 text-sm border border-gray-300 dark:border-jewel-border bg-white dark:bg-jewel-surface text-gray-900 dark:text-jewel-text rounded-lg outline-none focus:ring-2 focus:ring-blue-500 w-28"
          />
          <span className="text-gray-400 dark:text-jewel-text-muted">—</span>
          <DatePicker
            selected={endDateFilter}
            onChange={setEndDateFilter}
            selectsEnd
            startDate={startDateFilter}
            endDate={endDateFilter}
            minDate={startDateFilter}
            locale="el"
            dateFormat="dd/MM/yyyy"
            placeholderText="Έως"
            className="px-3 py-2 text-sm border border-gray-300 dark:border-jewel-border bg-white dark:bg-jewel-surface text-gray-900 dark:text-jewel-text rounded-lg outline-none focus:ring-2 focus:ring-blue-500 w-28"
          />
          {(startDateFilter || endDateFilter) && (
            <button
              onClick={() => { setStartDateFilter(null); setEndDateFilter(null); }}
              className="p-2 text-red-500 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors"
              title="Καθαρισμός"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-gray-400 dark:text-jewel-text-muted" />
        </div>
      ) : loadError ? (
        <div className="text-center py-12 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <p className="text-sm text-red-600 dark:text-red-400">{loadError}</p>
        </div>
      ) : filteredRequests.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 dark:bg-jewel-surface rounded-lg border-2 border-dashed border-gray-300 dark:border-jewel-border">
          <p className="text-sm text-gray-400 dark:text-jewel-text-muted">
            {searchTerm || startDateFilter || endDateFilter
              ? 'Δεν βρέθηκαν αιτήσεις για τα επιλεγμένα κριτήρια.'
              : 'Δεν υπάρχουν αιτήσεις Κανονικής Άδειας ή Άνευ Αποδοχών.'}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredRequests.map(req => {
            const currentType = (req.leave_type || req.leaveType || '').toLowerCase();
            const typeInfo = TYPE_LABELS[currentType];
            const start = new Date(req.start_date || req.startDate);
            const end = new Date(req.end_date || req.endDate);
            const days = calculateDaysBetween(start, end, holidays);
            const isGranted = req.manager_granted || req.managerGranted;

            return (
              <div key={req.id} className="bg-white dark:bg-jewel-bg border border-gray-200 dark:border-jewel-border rounded-xl p-4">
                <div className="flex items-center justify-between gap-4 flex-wrap">
                  <div className="min-w-[200px]">
                    <p className="font-semibold text-gray-900 dark:text-jewel-text">{req.user_name}</p>
                    <p className="text-xs text-gray-500 dark:text-jewel-text-muted">{req.department_name}</p>
                  </div>

                  <div className="text-sm text-gray-600 dark:text-jewel-text-muted">
                    {start.toLocaleDateString('el-GR')} — {end.toLocaleDateString('el-GR')}
                    <span className="text-gray-400 dark:text-jewel-text-muted"> ({days} εργ. ημ.)</span>
                  </div>

                  <div className="flex items-center gap-2">
                    <span
                      className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold"
                      style={{ backgroundColor: `${typeInfo.color}15`, color: typeInfo.color }}
                    >
                      {typeInfo.icon} {typeInfo.label}
                    </span>
                    {isGranted && (
                      <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-jewel-ruby-tint">
                        Χορηγήθηκε
                      </span>
                    )}
                    <span className="text-[10px] text-gray-400 dark:text-jewel-text-muted">
                      {STATUS_LABELS[req.status] || req.status}
                    </span>
                  </div>

                  <button
                    onClick={() => requestConversion(req)}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-lg border border-gray-200 dark:border-jewel-border text-gray-600 dark:text-jewel-text-muted hover:border-blue-400 dark:hover:border-jewel-teal hover:text-blue-600 dark:hover:text-jewel-teal-tint transition-colors flex-shrink-0"
                  >
                    <RefreshCw className="h-3.5 w-3.5" />
                    Μετατροπή σε {currentType === 'vacation' ? 'Άνευ Αποδοχών' : 'Κανονική Άδεια'}
                  </button>
                </div>
                {req.reason && (
                  <p className="text-xs text-gray-500 dark:text-jewel-text-muted italic mt-2 pt-2 border-t border-gray-100 dark:border-jewel-border">
                    "{req.reason}"
                  </p>
                )}
              </div>
            );
          })}
        </div>
      )}

      {confirmTarget && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-[100]">
          <div className="bg-white dark:bg-jewel-bg rounded-2xl shadow-2xl w-full max-w-sm p-6">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="h-5 w-5 text-amber-500" />
              </div>
              <h3 className="text-base font-semibold text-gray-900 dark:text-jewel-text">Επιβεβαίωση Μετατροπής</h3>
            </div>
            <p className="text-sm text-gray-600 dark:text-jewel-text-muted mb-5">
              Η αίτηση του/της <strong className="text-gray-800 dark:text-jewel-text">{confirmTarget.request.user_name}</strong> ({new Date(confirmTarget.request.start_date || confirmTarget.request.startDate).toLocaleDateString('el-GR')} — {new Date(confirmTarget.request.end_date || confirmTarget.request.endDate).toLocaleDateString('el-GR')}) θα μετατραπεί από{' '}
              <strong style={{ color: TYPE_LABELS[(confirmTarget.request.leave_type || confirmTarget.request.leaveType || '').toLowerCase()].color }}>
                {TYPE_LABELS[(confirmTarget.request.leave_type || confirmTarget.request.leaveType || '').toLowerCase()].label}
              </strong>{' '}
              σε <strong style={{ color: TYPE_LABELS[confirmTarget.newType].color }}>{TYPE_LABELS[confirmTarget.newType].label}</strong>.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setConfirmTarget(null)}
                disabled={submitting}
                className="flex-1 px-4 py-2 border border-gray-300 dark:border-jewel-border text-gray-600 dark:text-jewel-text-muted rounded-lg hover:bg-gray-50 dark:hover:bg-jewel-surface text-sm font-semibold transition-colors"
              >
                Ακύρωση
              </button>
              <button
                onClick={confirmConversion}
                disabled={submitting}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 dark:bg-jewel-forest hover:bg-blue-700 dark:hover:bg-jewel-forest-tint disabled:opacity-50 text-white text-sm font-bold rounded-lg transition-colors"
              >
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                Επιβεβαίωση
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeaveTypeCorrectionView;